import { useMemo, useRef, useState } from "react";
import { AsYouType } from "libphonenumber-js";
import { Check, ChevronDown, Search } from "lucide-react";

import Bandeira from "../Bandeira";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { buscarPais, filtrarPaises } from "../../helpers/paises";
import { paisDoNumero } from "../../helpers/numeroWhatsapp";
import { cn } from "../../lib/utils";

// Campo de telefone com o país à esquerda.
//
// O valor que entra e sai continua sendo o que o sistema sempre guardou: só
// dígitos, com o código do país na frente ("5511987654321"). Nada muda no
// banco nem no que o WhatsApp recebe — muda só a digitação, que antes exigia
// saber o DDI de cabeça e escrever tudo colado.
//
// O país é deduzido do próprio número enquanto ninguém escolhe um à mão. Assim
// abrir um contato que já existe mostra a bandeira certa sem ter guardado país
// nenhum junto do cadastro.

// Fora do componente de propósito: é função pura e barata, e dentro de um
// useMemo o try/catch impede o compilador do React de memorizar o bloco.
const formatarLocal = (nacional, codigoPais) => {
	if (!nacional) return "";

	try {
		return new AsYouType(codigoPais).input(nacional);
	} catch {
		return nacional;
	}
};

const PhoneInput = ({
	value,
	onChange,
	paisPadrao = "BR",
	id,
	name,
	disabled = false,
	placeholder,
	className,
	autoFocus = false,
}) => {
	const [aberto, setAberto] = useState(false);
	const [busca, setBusca] = useState("");
	const [paisManual, setPaisManual] = useState(null);
	const campoRef = useRef(null);

	const digitos = String(value || "").replace(/\D/g, "");

	// Escolha à mão vence a dedução: quem trocou o país de propósito não quer
	// que o campo volte sozinho ao apagar um dígito.
	const codigoPais =
		paisManual || paisDoNumero(digitos) || paisPadrao || "BR";

	const pais = buscarPais(codigoPais) || buscarPais("BR");
	const ddi = pais?.ddi || "";

	const nacional = digitos.startsWith(ddi) ? digitos.slice(ddi.length) : digitos;

	const paisesFiltrados = useMemo(() => filtrarPaises(busca), [busca]);

	// Formata enquanto digita, na forma do país escolhido.
	const exibido = formatarLocal(nacional, pais?.codigo);

	const emitir = (ddiNovo, nacionalNovo) => {
		const limpo = String(nacionalNovo || "").replace(/\D/g, "");

		// Campo vazio devolve string vazia, não o DDI sozinho: um contato com
		// "351" no lugar do telefone passaria em qualquer validação de
		// obrigatório e não serviria para nada.
		onChange(limpo ? `${ddiNovo}${limpo}` : "");
	};

	const escolherPais = (escolhido) => {
		setPaisManual(escolhido.codigo);
		setAberto(false);
		setBusca("");
		emitir(escolhido.ddi, nacional);
		campoRef.current?.focus();
	};

	return (
		<div className={cn("flex gap-2", className)}>
			<Popover open={aberto} onOpenChange={setAberto}>
				<PopoverTrigger asChild>
					<button
						type="button"
						disabled={disabled}
						aria-label={`País do telefone: ${pais?.nome || "não escolhido"}`}
						className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 text-sm ring-offset-background transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<Bandeira codigo={pais?.codigo} />
						<span className="text-muted-foreground">+{ddi}</span>
						<ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
					</button>
				</PopoverTrigger>
				<PopoverContent align="start" className="w-72 p-0">
					<div className="flex items-center gap-2 border-b px-3 py-2">
						<Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
						<input
							value={busca}
							onChange={(e) => setBusca(e.target.value)}
							placeholder="Procurar país ou código"
							className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
							autoFocus
						/>
					</div>
					<div className="max-h-64 overflow-y-auto py-1">
						{paisesFiltrados.length === 0 && (
							<p className="px-3 py-4 text-center text-xs text-muted-foreground">
								Nenhum país com esse nome ou código.
							</p>
						)}
						{paisesFiltrados.map((item) => (
							<button
								key={item.codigo}
								type="button"
								onClick={() => escolherPais(item)}
								className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
							>
								<Bandeira codigo={item.codigo} />
								<span className="flex-1 truncate">{item.nome}</span>
								<span className="text-xs text-muted-foreground">
									+{item.ddi}
								</span>
								{item.codigo === pais?.codigo && (
									<Check className="h-3.5 w-3.5 text-primary" />
								)}
							</button>
						))}
					</div>
				</PopoverContent>
			</Popover>

			<Input
				ref={campoRef}
				id={id}
				name={name}
				value={exibido}
				disabled={disabled}
				autoFocus={autoFocus}
				inputMode="tel"
				autoComplete="tel-national"
				placeholder={placeholder || "912 345 678"}
				onChange={(e) => emitir(ddi, e.target.value)}
				onPaste={(e) => {
					// Colar o número completo, com DDI, é o caso mais comum: vem de
					// uma conversa, de um e-mail, de outra tela. Sem tratar aqui, o
					// "+351" colado viraria parte do número nacional e o telefone
					// sairia com o país duplicado.
					const texto = e.clipboardData.getData("text");

					if (!texto.trim().startsWith("+")) return;

					e.preventDefault();

					const colado = texto.replace(/\D/g, "");

					if (!colado) return;

					setPaisManual(null);
					onChange(colado);
				}}
			/>
		</div>
	);
};

export default PhoneInput;
